import type { Todo } from "../../domain/entities.ts";

export interface ComposerRecoveryCapabilities {
  activeRootId(): string | null;
  panelDraft(): Todo | null;
  selectedTaskId(): string | null;
  recoveredEditDraft(): Todo | null;
  setRecoveredEditDraft(todo: Todo | null): void;
  task(id: string): Todo | undefined;
  applyForm(todo: Todo, form: HTMLFormElement, data: FormData, externalCalendars: Array<"google" | "microsoft">): void;
  persist(key: string, value: unknown): Promise<void>;
}

export function composerRecoveryKey(rootId: string | null): string {
  return `taskComposerRecovery:${rootId || "none"}`;
}

export function createComposerRecoveryController(capabilities: ComposerRecoveryCapabilities) {
  let timer: number | null = null;

  const clearTimer = () => {
    if (timer !== null) window.clearTimeout(timer);
    timer = null;
  };

  const save = async (form: HTMLFormElement): Promise<void> => {
    const creating = form.dataset.mode === "create";
    const draft = creating ? capabilities.panelDraft() : capabilities.recoveredEditDraft() || capabilities.task(form.dataset.id || "");
    if (!draft) return;
    const data = new FormData(form);
    const reminderTimes = data.getAll("exactReminderAt").map(String).filter(Boolean);
    if (reminderTimes.some(value => Number.isNaN(new Date(value).getTime()))) return;
    const externalCalendars = data.getAll("externalCalendars").map(String).filter((provider): provider is "google" | "microsoft" => provider === "google" || provider === "microsoft");
    const recovered = structuredClone(draft);
    capabilities.applyForm(recovered, form, data, externalCalendars);
    if (creating) await capabilities.persist(composerRecoveryKey(capabilities.activeRootId()), recovered);
    else {
      capabilities.setRecoveredEditDraft(recovered);
      await capabilities.persist(`taskEditRecovery:${form.dataset.id}`, recovered);
    }
  };

  return {
    clearTimer,
    schedule(form: HTMLFormElement): void {
      if (form.dataset.mode === "create" ? !capabilities.panelDraft() : !capabilities.selectedTaskId()) return;
      clearTimer();
      timer = window.setTimeout(() => { timer = null; void save(form); }, 300);
    },
    save,
    async clearCreate(): Promise<void> {
      clearTimer();
      await capabilities.persist(composerRecoveryKey(capabilities.activeRootId()), null);
    },
    async clearEdit(id: string): Promise<void> {
      capabilities.setRecoveredEditDraft(null);
      await capabilities.persist(`taskEditRecovery:${id}`, null);
    },
  };
}
